import React, {useEffect, useMemo, useState} from 'react';
import {fetchData} from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import PageLayout from '../Layout/PageLayout';
import {Card, DataTable, Tabs, StatusBadge, StatCard, tokens} from '../UI';

const kes = (n) => `KES ${(Number(n) || 0).toFixed(2)}`;

// Human labels + badge tones for the payment target and its settlement state.
const TYPE_LABEL = {
    request: 'Delivery',
    business: 'Activation fee',
    invoice: 'Invoice',
};
const typeTone = (t) => (t === 'invoice' ? 'brand' : t === 'business' ? 'warn' : 'neutral');
const statusTone = (s) => (String(s).toUpperCase() === 'PAID' ? 'success' : 'neutral');

const amountOf = (row) => Number(row.processed_amount) || Number(row.initial_amount) || 0;

const columns = [
    {
        name: 'Date',
        grow: 1.4,
        cell: (row) => row.date_string || new Date(row.created_at).toLocaleString(),
    },
    {
        name: 'Type',
        grow: 1,
        cell: (row) => (
            <StatusBadge tone={typeTone(row.account_type)}>
                {TYPE_LABEL[row.account_type] || row.account_type}
            </StatusBadge>
        ),
    },
    {name: 'Description', grow: 2.4, cell: (row) => row.description || '—'},
    {name: 'Customer', grow: 1.2, cell: (row) => row.customer_phone || '—'},
    {name: 'Channel', grow: 0.9, cell: (row) => row.channel || '—'},
    {
        name: 'Amount',
        grow: 1,
        right: true,
        cell: (row) => <span style={{fontWeight: 600}}>{row.formatted_amount ? `KES ${row.formatted_amount}` : kes(amountOf(row))}</span>,
    },
    {
        name: 'Status',
        grow: 0.9,
        cell: (row) => <StatusBadge tone={statusTone(row.status)}>{row.status || 'PENDING'}</StatusBadge>,
    },
];

export default function TransactionsList() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [typeTab, setTypeTab] = useState('all');
    const [statusTab, setStatusTab] = useState('all');

    useEffect(() => {
        setLoading(true);
        const params = {limit: '500'};
        if (typeTab !== 'all') params.account_type = typeTab;
        if (statusTab !== 'all') params.status = statusTab;
        const qs = new URLSearchParams(params).toString();
        fetchData(`${Endpoints.TRANSACTION_LIST_ADMIN}?${qs}`)
            .then((res) => res.json())
            .then((res) => {
                setList(res.data || []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [typeTab, statusTab]);

    // Totals for the visible set — settled money in vs. still pending.
    const totals = useMemo(() => {
        let paid = 0, pending = 0, paidCount = 0;
        for (const t of list) {
            const amt = amountOf(t);
            if (String(t.status).toUpperCase() === 'PAID') {
                paid += amt;
                paidCount += 1;
            } else {
                pending += amt;
            }
        }
        return {paid, pending, paidCount, count: list.length};
    }, [list]);

    return (
        <PageLayout title="Transactions">
            <div style={{display: 'flex', gap: tokens.spacing[4], flexWrap: 'wrap', marginBottom: tokens.spacing[4]}}>
                <StatCard style={{flex: '1 1 200px'}} label="Settled (shown)" value={kes(totals.paid)} accent/>
                <StatCard style={{flex: '1 1 200px'}} label="Pending (shown)" value={kes(totals.pending)}/>
                <StatCard style={{flex: '1 1 200px'}} label="Settled count" value={String(totals.paidCount)}/>
                <StatCard style={{flex: '1 1 200px'}} label="Transactions" value={String(totals.count)}/>
            </div>

            <Tabs
                tabs={[
                    {key: 'all', label: 'All types'},
                    {key: 'request', label: 'Deliveries'},
                    {key: 'invoice', label: 'Invoices'},
                    {key: 'business', label: 'Activation fees'},
                ]}
                value={typeTab}
                onChange={setTypeTab}
            />

            <Tabs
                tabs={[
                    {key: 'all', label: 'All statuses'},
                    {key: 'PAID', label: 'Settled'},
                    {key: 'PENDING', label: 'Pending'},
                ]}
                value={statusTab}
                onChange={setStatusTab}
            />

            <Card padded={false}>
                <DataTable
                    columns={columns}
                    data={list}
                    progressPending={loading}
                    pagination
                    paginationPerPage={25}
                    noHeader={true}
                />
            </Card>
        </PageLayout>
    );
}
